import React from 'react';
import { Box, VStack, HStack, Text, Button, Heading, Avatar, Progress, Stat, StatNumber, StatHelpText } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';

const MotionBox = motion(Box);

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, setCurrentUser, isMensalista } = useApp();

  // Redirecionar para a página inicial se não houver usuário logado
  React.useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Efeito para buscar a foto do perfil (executa apenas uma vez)
  React.useEffect(() => {
    async function fetchProfilePhoto() {
      if (!currentUser?.name) return;
      
      const { data: mensalistaData } = await supabase
        .from('mensalistas')
        .select('foto_url')
        .ilike('nome', currentUser.name)
        .single();

      if (mensalistaData?.foto_url && !currentUser.photoUrl) {
        setCurrentUser(prev => ({
          ...prev,
          photoUrl: mensalistaData.foto_url
        }));
      }
    }
    fetchProfilePhoto();
  }, [currentUser?.name, currentUser?.photoUrl, setCurrentUser]); // Adiciona dependências necessárias

  // Histórico real de serviços do usuário
  const [serviceHistory, setServiceHistory] = React.useState([]);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  // Paginação do histórico de serviços
  const [currentHistoryPage, setCurrentHistoryPage] = React.useState(1);
  const historyPageSize = 10;
  const historyTotalPages = Math.ceil(serviceHistory.length / historyPageSize);
  const paginatedHistory = serviceHistory.slice((currentHistoryPage - 1) * historyPageSize, currentHistoryPage * historyPageSize);

  // NOVO: Controle de uso dos serviços do pacote
  const [usoServicos, setUsoServicos] = React.useState([]);
  const [loadingUsoServicos, setLoadingUsoServicos] = React.useState(false);

  // NOVO: Limites do pacote do mensalista
  const [pacoteServicos, setPacoteServicos] = React.useState([]);
  const [loadingPacoteServicos, setLoadingPacoteServicos] = React.useState(false);
  
  // Estado para o barbeiro preferido
  const [barbeiroPref, setBarbeiroPref] = React.useState('');
  const [loadingBarbeiro, setLoadingBarbeiro] = React.useState(false);

  // Buscar barbeiro preferido do mensalista
  React.useEffect(() => {
    async function fetchBarbeiroPref() {
      if (!currentUser || !isMensalista) {
        setBarbeiroPref('');
        return;
      }
      setLoadingBarbeiro(true);
      // Buscar mensalista pelo nome para pegar o barbeiro preferido
      const { data: mensalistaData, error: mensalistaError } = await supabase
        .from('mensalistas')
        .select('barbeiro_id, barbeiros(nome)')
        .ilike('nome', currentUser.name)
        .single();
      
      if (!mensalistaError && mensalistaData && mensalistaData.barbeiros) {
        setBarbeiroPref(mensalistaData.barbeiros.nome);
      } else {
        setBarbeiroPref('');
      }
      setLoadingBarbeiro(false);
    }
    fetchBarbeiroPref();
  }, [currentUser, isMensalista]);

  React.useEffect(() => {
    async function fetchServiceHistory() {
      // Buscar limites e uso dos serviços do pacote do mensalista
      if (isMensalista && currentUser) {
        setLoadingUsoServicos(true);
        setLoadingPacoteServicos(true);

        try {
          // 1. Buscar o mensalista e seu pacote
          const { data: mensalistaData } = await supabase
            .from('mensalistas')
            .select('id, pacote_id')
            .ilike('nome', currentUser.name)
            .single();

          if (mensalistaData?.pacote_id) {
            // 2. Buscar os serviços do pacote e seus limites
            const { data: servicosPacote } = await supabase
              .from('pacote_servicos')
              .select(`
                servico_id,
                quantidade_inclusa,
                servicos(id, nome)
              `)
              .eq('pacote_id', mensalistaData.pacote_id);

            // 3. Buscar data_inicio e data_fim do mensalista
            setLoadingUsoServicos(true);

            const { data: mensalistaPeriodo, error: errorPeriodo } = await supabase
              .from('mensalistas')
              .select('data_inicio, data_fim')
              .eq('id', mensalistaData.id)
              .single();

            if (errorPeriodo || !mensalistaPeriodo) {
              console.error('Erro ao buscar período do mensalista:', errorPeriodo);
              setLoadingUsoServicos(false);
              return;
            }

            const { data_inicio, data_fim } = mensalistaPeriodo;
            if (!data_inicio || !data_fim) {
              console.warn('Mensalista sem período definido.');
              setLoadingUsoServicos(false);
              return;
            }

            // Buscar usos do pacote dentro do período de vigência
            const { data: usos, error: errorUsos } = await supabase
              .from('mensalista_pacote_servico_uso')
              .select('servico_id, quantidade_utilizada, created_at')
              .eq('mensalista_id', mensalistaData.id)
              .gte('created_at', data_inicio)
              .lte('created_at', data_fim);

            if (errorUsos) {
              console.error('Erro ao buscar usos:', errorUsos);
              setLoadingUsoServicos(false);
              return;
            }

            // 2. Calcular totais por serviço
            const usosMap = {};
            if (usos && Array.isArray(usos)) {
              usos.forEach(u => {
                if (u.servico_id && u.quantidade_utilizada) {
                  usosMap[u.servico_id] = (usosMap[u.servico_id] || 0) + u.quantidade_utilizada;
                }
              });
            }

            // 3. Montar array final com limites e uso
            const pacoteServicosArr = servicosPacote.map(servico => ({
              servico_id: servico.servico_id,
              nome: servico.servicos?.nome || 'Serviço',
              quantidade_inclusa: servico.quantidade_inclusa,
              quantidade_usada: usosMap[servico.servico_id] || 0,
              quantidade_disponivel: Math.max(
                (servico.quantidade_inclusa || 0) - (usosMap[servico.servico_id] || 0),
                0
              )
            }));

            setPacoteServicos(pacoteServicosArr);

            // 6. Buscar histórico detalhado de uso, também filtrando pelo período
            const { data: historicoUso } = await supabase
              .from('mensalista_pacote_servico_uso')
              .select('servico_id, quantidade_utilizada, created_at, mes_referencia')
              .eq('mensalista_id', mensalistaData.id)
              .gte('created_at', data_inicio)
              .lte('created_at', data_fim)
              .order('created_at', { ascending: false });

            if (historicoUso) {
              // Depois buscar os nomes dos serviços
              const servicosIds = [...new Set(historicoUso.map(uso => uso.servico_id))];
              if (servicosIds.length > 0) {
                const { data: servicos } = await supabase
                  .from('servicos')
                  .select('id, nome')
                  .in('id', servicosIds);

                // Mapear os nomes dos serviços para o histórico
                const historicoComNomes = historicoUso.map(uso => ({
                  ...uso,
                  quantidade: uso.quantidade_utilizada, // Para manter compatibilidade com o template
                  data_uso: uso.created_at, // Para manter compatibilidade com o template
                  servicos: {
                    nome: servicos?.find(s => s.id === uso.servico_id)?.nome || 'Serviço'
                  }
                }));

                setUsoServicos(historicoComNomes);
              } else {
                setUsoServicos([]);
              }
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados do pacote:', error);
          setPacoteServicos([]);
          setUsoServicos([]);
        }

        setLoadingPacoteServicos(false);
        setLoadingUsoServicos(false);
      }

      if (!currentUser || !currentUser.name) {
        setServiceHistory([]);
        setLoadingHistory(false);
        return;
      }
      setLoadingHistory(true);
      
      // 1. Buscar todos os atendimentos do usuário pelo nome
      const { data: atendimentos, error: errorAt } = await supabase
        .from('atendimentos')
        .select('id')
        .eq('nome_cliente', currentUser.name);
        
      if (errorAt || !atendimentos || atendimentos.length === 0) {
        setServiceHistory([]);
        setLoadingHistory(false);
        return;
      }
      
      const atendimentoIds = atendimentos.map(a => a.id);
      
      // 2. Buscar todos os serviços realizados nesses atendimentos
      const { data, error } = await supabase
        .from('atendimentos_servicos')
        .select(`
          id, 
          atendimento_id, 
          servico_id, 
          quantidade, 
          valor_final,
          criado_em,
          servicos(nome)
        `)
        .in('atendimento_id', atendimentoIds)
        .order('criado_em', { ascending: false });
      if (!error && data) {
        const formatDate = (iso) => {
          if (!iso) return '';
          const d = new Date(iso);
          return d.toLocaleDateString('pt-BR');
        };
        // Mapeia os dados para o formato do histórico
        setServiceHistory(data.map(s => ({
          id: s.id,
          date: formatDate(s.criado_em),
          service: s.servicos?.nome || 'Serviço não especificado',
          valor: s.valor_final ? `R$${Number(s.valor_final).toFixed(2)}` : ''
        })));
      } else {
        setServiceHistory([]);
      }
      setLoadingHistory(false);
    }
    fetchServiceHistory();
  }, [currentUser, isMensalista]);

  const handleBack = () => {
    navigate('/menu'); // Navega diretamente para o Menu Principal
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      flex={1}
      overflowY="auto"
      p={6}
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}
    >
      {currentUser && (
        <VStack spacing={6} align="stretch">
          {/* Header com título e botão voltar */}
          <Box
            position="sticky"
            top={0}
            zIndex={10}
            bg="linear-gradient(180deg, rgba(26,32,44,0.95) 0%, rgba(26,32,44,0.8) 100%)"
            backdropFilter="blur(10px)"
            px={4}
            py={3}
            mb={4}
            borderRadius="xl"
            boxShadow="lg"
          >
            <HStack justifyContent="space-between" alignItems="center" width="100%">
              <Heading size="lg" color="white">Meu Perfil</Heading>
              <Button
                variant="solid"
                onClick={handleBack}
                bg="yellow.400"
                color="gray.800"
                _hover={{ bg: 'yellow.500' }}
                _active={{ bg: 'yellow.600' }}
                size="lg"
                borderRadius="xl"
                px={6}
              >
                Voltar
              </Button>
            </HStack>
          </Box>

          {/* Avatar e nome */}
          <VStack spacing={4} align="center" width="100%" mb={2}>
            <Avatar size="2xl" name={currentUser.name} src={currentUser.photoUrl} bg="yellow.400" color="black" border="6px solid #fff" boxShadow="0 4px 32px #0007" mb={2} />
            <Text fontSize="2xl" fontWeight="bold" color="yellow.300">{currentUser.name}</Text>
          </VStack>

          {/* Limites do pacote do mensalista */}
          {isMensalista && (
            <Box 
              mt={6} 
              p={6} 
              bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
              borderRadius="2xl"
              boxShadow="2xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              backdropFilter="blur(10px)"
            >
              <HStack mb={6} justify="space-between" align="center">
                <Heading size="md" color="yellow.400" letterSpacing="wide">
                  📃 Meu Pacote de Serviços
                </Heading>
              </HStack>
              <VStack spacing={4} align="stretch">
                {loadingPacoteServicos ? (
                  <Text color="gray.300">Carregando limites do pacote...</Text>
                ) : pacoteServicos.length === 0 ? (
                  <Text color="gray.400">Nenhum pacote ativo encontrado.</Text>
                ) : (
                  pacoteServicos.map((servico) => {
                    const percentUsed = Math.min((servico.quantidade_usada / servico.quantidade_inclusa) * 100, 100);
                    const isAlmostFull = percentUsed >= 80;
                    const isFull = percentUsed >= 100;
                    const gradientColor = isFull ? "red.500" : isAlmostFull ? "yellow.400" : "green.400";
                    
                    return (
                      <Box 
                        key={servico.servico_id} 
                        p={5} 
                        bg="whiteAlpha.50"
                        _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                        borderRadius="xl"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                      >
                        <HStack justify="space-between" mb={3}>
                          <HStack spacing={3}>
                            <Box
                              p={2}
                              borderRadius="lg"
                              bg={`${gradientColor}20`}
                              color={gradientColor}
                            >
                              {isFull ? '✖️' : isAlmostFull ? '⚠️' : '✅'}
                            </Box>
                            <Text fontSize="lg" color="white" fontWeight="bold">
                              {servico.nome}
                            </Text>
                          </HStack>
                          <Stat textAlign="right" flexGrow={0}>
                            <StatNumber 
                              color={gradientColor}
                              fontSize="2xl"
                              fontWeight="bold"
                            >
                              {servico.quantidade_disponivel}
                            </StatNumber>
                            <StatHelpText color="gray.400" fontSize="xs" mt={0}>
                              restantes
                            </StatHelpText>
                          </Stat>
                        </HStack>
                        
                        <Box 
                          position="relative" 
                          mb={3}
                          overflow="hidden"
                          borderRadius="full"
                          bg="whiteAlpha.100"
                          boxShadow="inner"
                        >
                          <Progress
                            value={percentUsed}
                            size="lg"
                            borderRadius="full"
                            sx={{
                              bg: 'transparent',
                              '& > div': {
                                background: `linear-gradient(90deg, ${gradientColor}80, ${gradientColor})`,
                                transition: 'all 0.3s ease',
                              }
                            }}
                            height="32px"
                          />
                          <Text
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            fontSize="sm"
                            fontWeight="bold"
                            color="white"
                            textShadow="0 2px 4px rgba(0,0,0,0.5)"
                          >
                            {Math.round(percentUsed)}%
                          </Text>
                        </Box>
                        
                        <HStack justify="space-between" fontSize="sm">
                          <Text color="gray.300">{servico.quantidade_usada} usado(s)</Text>
                          <Text color="gray.300">Total: {servico.quantidade_inclusa}</Text>
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </VStack>
            </Box>
          )}

          {/* Histórico de Uso */}
          {isMensalista && (
            <Box 
              mt={6} 
              p={6} 
              bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
              borderRadius="2xl"
              boxShadow="2xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              backdropFilter="blur(10px)"
            >
              <HStack mb={6} justify="space-between" align="center">
                <Heading size="md" color="yellow.400" letterSpacing="wide">
                  📊 Histórico de Uso
                </Heading>
              </HStack>
              <VStack spacing={3} align="stretch">
                {loadingUsoServicos ? (
                  <Text color="gray.300">Carregando...</Text>
                ) : usoServicos.length === 0 ? (
                  <Text color="gray.400">Nenhum serviço do pacote utilizado ainda.</Text>
                ) : (
                  usoServicos.map((uso, index) => (
                    <Box 
                      key={index} 
                      p={4} 
                      bg="whiteAlpha.50"
                      _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                      transition="all 0.2s"
                      borderRadius="xl"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <HStack spacing={4} align="center">
                        <Box
                          p={2}
                          borderRadius="lg"
                          bg="yellow.40020"
                          color="yellow.400"
                        >
                          ✂️
                        </Box>
                        <Box>
                          <Text fontSize="lg" color="white" fontWeight="bold">
                            {uso.servicos?.nome}
                          </Text>
                          <Text color="gray.400" fontSize="sm">
                            {new Date(uso.data_uso).toLocaleDateString()}
                          </Text>
                        </Box>
                      </HStack>
                      <Box 
                        py={1} 
                        px={3} 
                        bg="yellow.40020"
                        borderRadius="full"
                        border="1px solid"
                        borderColor="yellow.400"
                      >
                        <Text color="yellow.400" fontSize="md" fontWeight="bold">
                          {uso.quantidade}x
                        </Text>
                      </Box>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>
          )}

          {/* Histórico de Serviços */}
           <Box
             mt={6}
             p={6}
             bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
             borderRadius="2xl"
             boxShadow="2xl"
             border="1px solid"
             borderColor="whiteAlpha.100"
             backdropFilter="blur(10px)"
           >
             <HStack mb={6} justify="space-between" align="center">
               <Heading size="md" color="yellow.400" letterSpacing="wide">
                 ✂️ Histórico de Serviços
               </Heading>
             </HStack>
             <VStack 
               spacing={4}
               align="stretch"
               bg="transparent"
               borderRadius="none"
               p={0}
             >
               {loadingHistory ? (
                 <Text color="gray.300">Carregando histórico...</Text>
               ) : serviceHistory.length === 0 ? (
                 <Text color="gray.400">Nenhum serviço realizado encontrado.</Text>
               ) : (
                 paginatedHistory.map((service, index) => (
                   <Box 
                     key={service.id || index} 
                     bg="whiteAlpha.50"
                     _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                     transition="all 0.2s"
                     borderRadius="xl"
                     boxShadow="lg"
                     border="1px solid"
                     borderColor="whiteAlpha.200"
                     p={4}
                   >
                     <HStack spacing={4} align="center" justify="space-between">
                       <HStack spacing={3} flex={1}>
                         <Box
                           p={2}
                           borderRadius="lg"
                           bg="yellow.40020"
                           color="yellow.400"
                         >
                           ✂️
                         </Box>
                         <Box>
                           <Text fontSize="lg" color="white" fontWeight="bold">
                             {service.service}
                           </Text>
                           <Text color="gray.400" fontSize="sm">
                             {service.date}
                           </Text>
                         </Box>
                       </HStack>
                       {service.valor && (
                         <Box 
                           py={1} 
                           px={3} 
                           bg="green.40020"
                           borderRadius="full"
                           border="1px solid"
                           borderColor="green.400"
                         >
                           <Text color="green.400" fontSize="md" fontWeight="bold">
                             {service.valor}
                           </Text>
                         </Box>
                       )}
                     </HStack>
                   </Box>
                 ))
               )}
             </VStack>
             {/* Paginação */}
             {serviceHistory.length > 0 && (
               <HStack justify="center" mt={6} spacing={4}>
                 <Button
                   size="lg"
                   onClick={() => setCurrentHistoryPage((p) => Math.max(1, p - 1))}
                   isDisabled={currentHistoryPage === 1}
                   variant="solid"
                   bg="whiteAlpha.200"
                   color="white"
                   _hover={{ bg: 'whiteAlpha.300' }}
                   _active={{ bg: 'whiteAlpha.400' }}
                   borderRadius="xl"
                   width="120px"
                   height="60px"
                 >
                   ◀ Anterior
                 </Button>
                 <VStack spacing={1}>
                   <Text color="white" fontSize="lg" fontWeight="bold">
                     Página {currentHistoryPage}
                   </Text>
                   <Text color="gray.400" fontSize="sm">
                     de {historyTotalPages}
                   </Text>
                 </VStack>
                 <Button
                   size="lg"
                   onClick={() => setCurrentHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                   isDisabled={currentHistoryPage === historyTotalPages}
                   variant="solid"
                   bg="whiteAlpha.200"
                   color="white"
                   _hover={{ bg: 'whiteAlpha.300' }}
                   _active={{ bg: 'whiteAlpha.400' }}
                   borderRadius="xl"
                   width="120px"
                   height="60px"
                 >
                   Próxima ▶
                 </Button>
               </HStack>
             )}
           </Box>

           {/* Preferências - Apenas Barbeiro Preferido */}
           {isMensalista && (
             <Box 
               mt={6} 
               p={6} 
               bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
               borderRadius="2xl"
               boxShadow="2xl"
               border="1px solid"
               borderColor="whiteAlpha.100"
               backdropFilter="blur(10px)"
             >
               <HStack mb={6} justify="space-between" align="center">
                 <Heading size="md" color="yellow.400" letterSpacing="wide">
                   👨‍🦱 Barbeiro Preferido
                 </Heading>
               </HStack>
               
               <Box 
                 p={4} 
                 bg="whiteAlpha.50"
                 _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                 transition="all 0.2s"
                 borderRadius="xl"
                 boxShadow="lg"
                 border="1px solid"
                 borderColor="whiteAlpha.200"
               >
                 <HStack spacing={4} align="center">
                   <Box
                     p={2}
                     borderRadius="lg"
                     bg="yellow.40020"
                     color="yellow.400"
                   >
                     👨‍🦱
                   </Box>
                   <Box>
                     {loadingBarbeiro ? (
                       <Text color="gray.300" fontSize="lg">Carregando...</Text>
                     ) : barbeiroPref ? (
                       <Text color="white" fontSize="lg" fontWeight="bold">{barbeiroPref}</Text>
                     ) : (
                       <Text color="gray.400" fontSize="lg">Nenhum barbeiro preferido definido</Text>
                     )}
                   </Box>
                 </HStack>
               </Box>
             </Box>
           )}
        </VStack>
      )}
    </MotionBox>
  );
};

export default ProfilePage;
